import { appRoutes, type RouteName, type RouteParamsFor } from '../routes';

/**
 * Concrete redirect target — a typed route name plus optional params.
 *
 * Use this when a redirect needs path or search params (e.g. opening
 * the CRM workspace at a specific client → `{ name: 'crm', params: { clientId } }`).
 */
export interface RedirectConfig<T extends RouteName = RouteName> {
  /** The route to redirect to. */
  name: T;
  /**
   * Params for the route, if any. Path params (`:id`) are substituted,
   * remaining keys become search params.
   */
  params?: RouteParamsFor<T>;
}

/**
 * Generic "where should the back button go?" descriptor. Accepted shapes:
 *
 * | Value | Meaning |
 * |---|---|
 * | `false` (or `undefined`) | Hide the back button. Same as not providing the prop. |
 * | `true` | Show the button, default redirect to the carrier-dashboard "More" screen (`settings`). |
 * | `'route_name'` | Show the button, redirect to the given typed route. |
 * | `{ name, params? }` | Show the button, redirect to the given route with params. |
 *
 * The boolean form is intentional sugar for the common case ("just take
 * me back to More") so consumers don't have to import `RouteName` just
 * to opt in.
 */
export type RedirectProp = boolean | RouteName | RedirectConfig;

/**
 * Default redirect target when the consumer passes `redirect={true}`.
 * "More" (`settings`) is the carrier-dashboard parent screen for every
 * sub-page that gets a `<PageBack>` (Users, Pricing, Contacts, Places,
 * Profile), so this is the most useful default.
 */
export const DEFAULT_REDIRECT_NAME: RouteName = 'settings';

/**
 * Type guard: is `value` one of the keys in `appRoutes`?
 *
 * Used by `<PageBack>` and the URL parser below to safely narrow an
 * arbitrary string (e.g. a `?back=` value) into a `RouteName` before
 * navigating.
 */
export function isRouteName(value: unknown): value is RouteName {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(appRoutes, value);
}

/**
 * Resolve a {@link RedirectProp} into the concrete `{ name, params? }`
 * shape used by `useNav()`. Returns `null` when the prop says "hide me".
 *
 * - `false` / `undefined` → `null` (hide)
 * - `true`                → `{ name: DEFAULT_REDIRECT_NAME }`
 * - `'name'`              → `{ name: 'name' }`
 * - `{ name, params }`    → returned as-is
 */
export function resolveRedirect(
  prop: RedirectProp | undefined,
): RedirectConfig | null {
  if (prop === undefined || prop === false) return null;
  if (prop === true) return { name: DEFAULT_REDIRECT_NAME };
  if (typeof prop === 'string') {
    return isRouteName(prop) ? { name: prop } : null;
  }
  return prop;
}

/**
 * Parse a `?back=` URL search param into a {@link RedirectProp}.
 *
 * Accepted query values (case-sensitive):
 * - `?back=1`            → `true` (use default redirect)
 * - `?back=settings`     → `'settings'` (any registered route name)
 * - `?back=more`         → alias for `?back=settings` (legacy alias for the More screen)
 *
 * Anything else returns `undefined` so the caller can fall back to its
 * `redirect` prop. Hosts that want to stay strict can wrap this with
 * `isRouteName` + their own validation.
 */
export function parseUrlRedirect(
  searchParams: URLSearchParams,
): RedirectProp | undefined {
  const raw = searchParams.get('back');
  if (raw === null) return undefined;
  if (raw === '1' || raw === 'true') return true;
  if (raw === 'more') return DEFAULT_REDIRECT_NAME;
  if (isRouteName(raw)) return raw;
  return undefined;
}